import React, { useState, useEffect } from 'react';
import { apiEndpoints } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import EventForm from '../components/EventForm';
import AccidentForm from '../components/AccidentForm';
import CostForm from '../components/CostForm';
import Modal from '../components/Modal';
import VehicleStatsContent from '../components/VehicleStatsContent';
import { costTypeColor } from '../utils/costTypes';


const CostsCard = ({ costs, onAdd }) => {
  const totalRON = costs.reduce((s, c) => s + (Number(c.amount) || 0), 0);
  const preview = costs.slice(0, 6);
  return (
    <div className="card">
      <div className="card-header">
        <div className="card-title">Vehicle Costs</div>
        <button className="btn btn-primary btn-sm" onClick={onAdd}>+ Add</button>
      </div>
      <div className="card-body">
        {costs.length === 0 ? (
          <div style={{ padding: '16px 0', textAlign: 'center', color: 'var(--text-3)', fontSize: '13px' }}>No costs recorded yet.</div>
        ) : (
          <>
            {preview.map((cost, i) => {
              const color = costTypeColor(cost.costType);
              return (
                <div key={cost._id || i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: i < preview.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                      <span style={{ width: 7, height: 7, borderRadius: '50%', background: color, flexShrink: 0 }} />
                      <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-1)', textTransform: 'capitalize' }}>{cost.costType || 'Cost'}</span>
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-3)', marginTop: '2px', paddingLeft: '14px' }}>
                      {cost.date ? new Date(cost.date).toLocaleDateString() : '—'}
                      {cost.description ? ` • ${cost.description.slice(0, 28)}${cost.description.length > 28 ? '…' : ''}` : ''}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: '8px' }}>
                    <span style={{ fontSize: '14px', fontWeight: 700, color: '#0077b6' }}>{Number(cost.amount || 0).toLocaleString()}</span>
                    <span style={{ fontSize: '10px', color: 'var(--text-3)', marginLeft: '3px' }}>{cost.currency || 'RON'}</span>
                  </div>
                </div>
              );
            })}
            {costs.length > 6 && (
              <div style={{ fontSize: '11px', color: 'var(--text-3)', textAlign: 'center', paddingTop: '6px' }}>+{costs.length - 6} more</div>
            )}
            <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '2px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-3)' }}>{costs.length} entr{costs.length === 1 ? 'y' : 'ies'}</span>
              <span style={{ fontSize: '15px', fontWeight: 800, color: 'var(--text-1)' }}>{totalRON.toLocaleString()} <span style={{ fontSize: '11px', fontWeight: 500, color: 'var(--text-3)' }}>RON</span></span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const DriverVehicles = () => {
  const { user } = useAppContext();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [showEventForm, setShowEventForm] = useState(false);
  const [showAccidentForm, setShowAccidentForm] = useState(false);
  const [showCostForm, setShowCostForm] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [vehicleCosts, setVehicleCosts] = useState([]);
  const [vehicleAccidents, setVehicleAccidents] = useState([]);
  const [docPreview, setDocPreview] = useState(null);

  useEffect(() => {
    apiEndpoints.vehicles.list()
      .then(res => {
        const mine = (res.data || []).filter(v => v.driverId && v.driverId === user?.driverId);
        setVehicles(mine);
      })
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const refreshCosts = (plate) => {
    apiEndpoints.costs.list()
      .then(res => setVehicleCosts((res.data || []).filter(c => c.plate === plate).sort((a, b) => new Date(b.date) - new Date(a.date))))
      .catch(() => setVehicleCosts([]));
  };

  const refreshAccidents = (plate) => {
    apiEndpoints.accidents.list()
      .then(res => setVehicleAccidents((res.data || []).filter(a => a.plate === plate)))
      .catch(() => setVehicleAccidents([]));
  };

  useEffect(() => {
    if (!selectedVehicle?.plate) { setVehicleCosts([]); setVehicleAccidents([]); return; }
    refreshCosts(selectedVehicle.plate);
    refreshAccidents(selectedVehicle.plate);
  }, [selectedVehicle?.plate]); // eslint-disable-line

  const handleSaveEvent = async (payload) => {
    try {
      await apiEndpoints.events.create({ ...payload, resp: user.name });
      setShowEventForm(false);
    } catch {
      alert('Error saving event. Please try again.');
    }
  };

  const handleSaveAccident = async (payload, file) => {
    try {
      await apiEndpoints.accidents.create({ ...payload, driverName: user.name, driverId: user.driverId || '' }, file);
      setShowAccidentForm(false);
      if (selectedVehicle?.plate) refreshAccidents(selectedVehicle.plate);
    } catch {
      alert('Error saving accident. Please try again.');
    }
  };

  const handleSaveCost = async (payload, file) => {
    try {
      await apiEndpoints.costs.create({ ...payload, driverId: user.driverId || '' }, file);
      setShowCostForm(false);
      if (selectedVehicle?.plate) refreshCosts(selectedVehicle.plate);
    } catch {
      alert('Error saving cost. Please try again.');
    }
  };

  if (loading) return <div className="page-loading">Loading vehicles…</div>;

  // ── DETAIL VIEW ──────────────────────────────────────────────────────────────
  if (selectedVehicle) {
    const plate = selectedVehicle.plate || 'N/A';
    const activeEvents = selectedVehicle.events || [];
    const rca = selectedVehicle.docs?.rca;
    const itp = selectedVehicle.docs?.itp;
    const casco = selectedVehicle.docs?.casco;

    const docRow = (label, doc) => {
      const hasFile = !!doc?.fileUrl;
      const fileUrl = hasFile
        ? `${process.env.REACT_APP_API_URL || 'http://localhost:4000/api'}/static/${doc.fileUrl}`
        : null;
      return (
        <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', background: hasFile ? 'transparent' : '#fff5f5', borderBottom: label === 'CASCO — Optional Insurance' ? 'none' : '1px solid #f3f4f6' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</div>
            <div style={{ fontSize: '11px', color: hasFile ? '#10b981' : '#ef4444', marginTop: '2px', fontWeight: 500 }}>
              {hasFile ? (doc.issuer && doc.issuer !== '—' ? doc.issuer : 'On file') : 'No file attached'}
            </div>
          </div>
          {hasFile
            ? <button className="btn btn-ghost btn-xs" style={{ color: '#0077b6', flexShrink: 0 }} onClick={() => setDocPreview({ url: fileUrl, label })}>Preview</button>
            : <span className="btn btn-ghost btn-xs" style={{ color: '#d1d5db', cursor: 'default', flexShrink: 0 }}>No file</span>
          }
        </div>
      );
    };

    return (
      <div>
        <button className="back-btn" onClick={() => setSelectedVehicle(null)}>
          <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
          </svg>
          Back to vehicles
        </button>

        <div className="vprofile-header">
          <div className="vprofile-plate"><span className="plate-pill">{plate}</span></div>
          <div className="vprofile-model">{selectedVehicle.brand} {selectedVehicle.model}</div>
          <div className="vprofile-sub">{selectedVehicle.year} • {selectedVehicle.fuel || '—'} • {selectedVehicle.company || '—'}</div>
        </div>

        <div className="detail-grid">
          {/* Vehicle Details */}
          <div className="card">
            <div className="card-header"><div className="card-title">Vehicle Details</div></div>
            <div className="card-body">
              <div className="info-row"><div className="info-lbl">Registration number</div><div className="info-val">{plate}</div></div>
              <div className="info-row"><div className="info-lbl">Brand / Model</div><div className="info-val">{selectedVehicle.brand} {selectedVehicle.model}</div></div>
              <div className="info-row"><div className="info-lbl">Year of manufacture</div><div className="info-val">{selectedVehicle.year || '—'}</div></div>
              <div className="info-row"><div className="info-lbl">Fuel type</div><div className="info-val">{selectedVehicle.fuel || '—'}</div></div>
              <div className="info-row"><div className="info-lbl">Body type</div><div className="info-val">{selectedVehicle.bodyType || selectedVehicle.type || '—'}</div></div>
              <div className="info-row"><div className="info-lbl">Color</div><div className="info-val">{selectedVehicle.color || '—'}</div></div>
              <div className="info-row"><div className="info-lbl">Company</div><div className="info-val">{selectedVehicle.company || '—'}</div></div>
              <div className="info-row"><div className="info-lbl">VIN / Chassis</div><div className="info-val">{selectedVehicle.vin || selectedVehicle.chassis || '—'}</div></div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="card quick-actions-card">
            <div className="card-header"><div className="card-title">Quick Actions</div></div>
            <div className="card-body" style={{ display: 'grid', gap: '10px' }}>
              <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => setShowEventForm(true)}>Add Event</button>
              <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => setShowAccidentForm(true)}>Report Accident</button>
              <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => setShowStatsModal(true)}>See Statistics</button>
              <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => setShowCostForm(true)}>Add Cost</button>
            </div>
          </div>

          {/* Acts & Documents — read-only */}
          <div className="card">
            <div className="card-header"><div className="card-title">Acts & Documents</div></div>
            <div className="card-body" style={{ padding: 0 }}>
              {docRow('RCA — Compulsory Insurance', rca)}
              {docRow('ITP — Technical Inspection', itp)}
              {docRow('CASCO — Optional Insurance', casco)}
            </div>
          </div>

          {/* Assigned Driver (me) */}
          <div className="card assigned-driver-card">
            <div className="card-header"><div className="card-title">Assigned Driver</div></div>
            <div className="card-body">
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
                <div className="avatar-circle">
                  {(user?.name || 'D').split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase()}
                </div>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-1)' }}>{user?.name || 'You'}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-3)' }}>Fleet driver • {selectedVehicle.company || '—'}</div>
                </div>
              </div>
              <div className="info-row" style={{ padding: '8px 0' }}>
                <div className="info-lbl">Status</div>
                <div className="info-val">{selectedVehicle.status || '—'}</div>
              </div>
              <div className="info-row" style={{ padding: '8px 0', borderBottom: 'none' }}>
                <div className="info-lbl">Driver ID</div>
                <div className="info-val">{user?.driverId || '—'}</div>
              </div>
            </div>
          </div>

          {/* Active Events */}
          <div className="card">
            <div className="card-header">
              <div className="card-title">Active Events</div>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowEventForm(true)}>+ Event</button>
            </div>
            <div className="card-body" style={{ overflowX: 'auto' }}>
              <div style={{ minWidth: '320px' }}>
                {activeEvents.length > 0 ? activeEvents.map((ev, i) => (
                  <div key={ev._id || i} className="info-row" style={{ padding: '12px 0' }}>
                    <div className="info-lbl">{ev.plate || plate}</div>
                    <div className="info-val">{ev.evt || ev.name || 'Event'}</div>
                    <div style={{ minWidth: '110px', textAlign: 'right', color: 'var(--text-3)' }}>{ev.created || ev.startDate || '—'}</div>
                  </div>
                )) : (
                  <div style={{ padding: '12px 0', color: 'var(--text-3)' }}>No active events.</div>
                )}
              </div>
            </div>
          </div>

          <CostsCard costs={vehicleCosts} onAdd={() => setShowCostForm(true)} />

          {/* Company */}
          <div className="card">
            <div className="card-header"><div className="card-title">Company</div></div>
            <div className="card-body">
              {selectedVehicle.company ? (
                <>
                  <div className="info-row"><div className="info-lbl">Company name</div><div className="info-val" style={{ fontWeight: 700 }}>{selectedVehicle.company}</div></div>
                  <div className="info-row"><div className="info-lbl">Fleet vehicles</div><div className="info-val">{vehicles.filter(v => v.company === selectedVehicle.company).length} vehicle(s)</div></div>
                  <div className="info-row" style={{ borderBottom: 'none' }}><div className="info-lbl">Active in company</div><div className="info-val">{vehicles.filter(v => v.company === selectedVehicle.company && v.status === 'Alocat').length} assigned</div></div>
                </>
              ) : (
                <div style={{ color: 'var(--text-3)', padding: '12px 0' }}>No company assigned.</div>
              )}
            </div>
          </div>

          {/* Accidents */}
          <div className="card">
            <div className="card-header">
              <div className="card-title">Accidents</div>
              <button className="btn btn-primary btn-sm" onClick={() => setShowAccidentForm(true)}>+ Report</button>
            </div>
            <div className="card-body">
              {vehicleAccidents.length === 0 ? (
                <div style={{ color: 'var(--text-3)', padding: '12px 0', fontSize: '13px' }}>No accidents recorded.</div>
              ) : (
                vehicleAccidents.slice(0, 5).map((a, i) => (
                  <div key={a._id || i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '8px 0', borderBottom: i < Math.min(vehicleAccidents.length, 5) - 1 ? '1px solid #f3f4f6' : 'none' }}>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: a.damageLevel === 'total_loss' ? '#7c3aed' : a.damageLevel === 'major' ? '#ef4444' : '#f59e0b' }}>
                        {a.damageLevel === 'total_loss' ? 'Total Loss' : a.damageLevel === 'major' ? 'Major' : 'Minor'}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--text-3)', marginTop: '2px' }}>
                        {a.date ? new Date(a.date).toLocaleDateString() : '—'}{a.location ? ` • ${a.location}` : ''}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: '8px' }}>
                      {a.repairCost
                        ? <span style={{ fontSize: '13px', fontWeight: 700, color: '#0077b6' }}>{Number(a.repairCost).toLocaleString()} {a.currency || 'RON'}</span>
                        : <span style={{ color: 'var(--text-3)', fontSize: '12px' }}>—</span>
                      }
                    </div>
                  </div>
                ))
              )}
              {vehicleAccidents.length > 5 && (
                <div style={{ fontSize: '11px', color: 'var(--text-3)', textAlign: 'center', paddingTop: '6px' }}>+{vehicleAccidents.length - 5} more</div>
              )}
            </div>
          </div>
        </div>

        <EventForm
          isOpen={showEventForm}
          onClose={() => setShowEventForm(false)}
          event={{ plate, car: `${selectedVehicle.brand} ${selectedVehicle.model}` }}
          onSave={handleSaveEvent}
        />
        <AccidentForm
          isOpen={showAccidentForm}
          onClose={() => setShowAccidentForm(false)}
          defaultPlate={plate}
          defaultCompany={selectedVehicle.company || ''}
          defaultVehicleName={`${selectedVehicle.brand || ''} ${selectedVehicle.model || ''}`.trim()}
          onSave={handleSaveAccident}
        />
        <CostForm
          isOpen={showCostForm}
          onClose={() => setShowCostForm(false)}
          cost={{ plate, company: selectedVehicle.company || '', vehicleName: `${selectedVehicle.brand || ''} ${selectedVehicle.model || ''}`.trim() }}
          onSave={handleSaveCost}
        />
        <Modal isOpen={showStatsModal} onClose={() => setShowStatsModal(false)} title="Vehicle Statistics" size="md">
          <VehicleStatsContent vehicle={selectedVehicle} />
        </Modal>

        {docPreview && (
          <div onClick={() => setDocPreview(null)} style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
            <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '12px', overflow: 'hidden', width: '100%', maxWidth: '860px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
                <span style={{ fontSize: '14px', fontWeight: 700, color: '#111827' }}>{docPreview.label}</span>
                <div style={{ display: 'flex', gap: '8px', marginLeft: '12px' }}>
                  <a href={docPreview.url} target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-xs" style={{ textDecoration: 'none', color: '#0077b6' }}>Open in tab</a>
                  <button onClick={() => setDocPreview(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: '2px' }}>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
                  </button>
                </div>
              </div>
              <div style={{ flex: 1, overflow: 'auto', background: '#f9fafb', minHeight: '300px' }}>
                {/\.(jpg|jpeg|png|gif|webp)$/i.test(docPreview.url)
                  ? <img src={docPreview.url} alt={docPreview.label} style={{ display: 'block', maxWidth: '100%', margin: '0 auto', padding: '16px' }} />
                  : <iframe src={docPreview.url} title={docPreview.label} style={{ width: '100%', height: '70vh', border: 'none', display: 'block' }} />
                }
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── LIST VIEW ────────────────────────────────────────────────────────────────
  return (
    <div>
      <div className="ph">
        <div>
          <div className="ph-title">My Vehicles</div>
          <div className="ph-sub">{vehicles.length} vehicle{vehicles.length !== 1 ? 's' : ''} assigned to you</div>
        </div>
      </div>

      {vehicles.length === 0 ? (
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center', padding: '48px 16px' }}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto 12px', display: 'block' }}>
              <path d="M5 17H3v-5l3.5-6H18l3 6v5h-2"/>
              <circle cx="7.5" cy="17" r="2.5"/>
              <circle cx="16.5" cy="17" r="2.5"/>
              <path d="M10 17h4"/>
            </svg>
            <div style={{ fontWeight: 600, color: '#374151', marginBottom: 4 }}>No vehicles assigned</div>
            <div style={{ fontSize: '13px', color: '#9ca3af' }}>Your assigned vehicles will appear here.</div>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="card-body vehicle-table-card">
            <table className="vehicle-table">
              <thead>
                <tr>
                  <th>REGISTRATION NUMBER</th>
                  <th>BRAND / MODEL</th>
                  <th>YEAR / FUEL</th>
                  <th>COMPANY</th>
                  <th>RCA</th>
                  <th>ITP</th>
                  <th>CASCO</th>
                  <th>STATUS</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {vehicles.map((vehicle) => {
                  const renderDoc = (doc) => {
                    const status = doc?.status || 'ok';
                    const label = status === 'critical' ? 'Critical' : status === 'expiring' ? 'Expire' : 'Valid';
                    return <span className={`status-pill ${status}`}>• {label}{doc?.exp ? ` ${doc.exp}` : ''}</span>;
                  };
                  return (
                    <tr key={vehicle.id || vehicle._id} onClick={() => setSelectedVehicle(vehicle)} style={{ cursor: 'pointer' }}>
                      <td className="cell-title"><span className="plate">{vehicle.plate}</span></td>
                      <td data-label="Brand / Model">
                        <div className="vehicle-name">{vehicle.brand} {vehicle.model}</div>
                        <div className="vehicle-sub">{vehicle.type || 'Vehicle'}</div>
                      </td>
                      <td data-label="Year / Fuel">{vehicle.year} · {vehicle.fuel}</td>
                      <td data-label="Company">{vehicle.company}</td>
                      <td data-label="RCA">{renderDoc(vehicle.docs?.rca)}</td>
                      <td data-label="ITP">{renderDoc(vehicle.docs?.itp)}</td>
                      <td data-label="CASCO">{renderDoc(vehicle.docs?.casco)}</td>
                      <td data-label="Status">
                        <span className={`status-pill ${vehicle.status === 'Disponibil' ? 'available' : vehicle.status === 'Alocat' ? 'assigned' : 'service'}`}>
                          {vehicle.status}
                        </span>
                      </td>
                      <td className="row-action cell-action">
                        <button className="btn btn-ghost btn-xs" onClick={e => { e.stopPropagation(); setSelectedVehicle(vehicle); }}>Open →</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default DriverVehicles;
